import { Picture, HoveredButton } from '@ui';
import type { FC } from 'react';
import { tipImg } from '@images';
import UtilsTime from '@utils/time';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import RecipeIngredients from './RecipeIngredients';
import { Link } from '@compat/router';
import icons from '@icons';
import { twcls } from '@utils/twcls';
import { declension } from '@utils/declension';
import RecipeActions from '@root/src/ui/components/RecipeCard/components/RecipeActions';
import dayjs from 'dayjs';
import type { GetRecipeByIdQuery } from '@schema/cart';
import RecipeSteps from './RecipeSteps';
import { observer } from 'mobx-react';
import { useStores } from '@root/src/stores';

const {
  playRounded: { white: playRoundedWhiteFilled }
} = icons.Fill;

const {
  // share: { darkGrey: shareDGOutlined },
  print: { black: printBlackOutlined, white: printWhiteOutlined },
  chevronRight: { lightGrey: crLGOutlined }
} = icons.Outline;

const {
  star: { yellow: starYellowFilled },
  person: { midGrey: personMidGreyFilled },
  messageCircle: {
    midGrey: messageCircleMidGreyFilled,
    white: messageCircleWhiteFilled
  },
  timer: { midGrey: timerMidGreyFilled },
  calendar: { darkGrey: calendarDGFilled, white: calendarWhiteFilled },
  // bookmark: { darkGrey: bookmarkDGFilled, red: bookmarkRedFilled },
  eye: { darkGrey: eyeDGFilled, white: eyeWhiteFilled },
  // share: { darkGrey: shareDarkGreyFilled },
  barChart2: { midGrey: barChart2MidGreyFilled },
  fire: { midGrey: fireMidGreyFilled },
  timer: { red: timerRedFilled },
  quotations: { red: quotationsRedFilled }
} = icons.Fill;

interface RecipeTipProps {
  isGrey: boolean;
  tip: string | null | undefined;
}
export const RecipeTip: FC<RecipeTipProps> = observer(({ tip, isGrey }) => {
  if (!tip) {
    return null;
  }
  return (
    <div
      className={twcls(
        'p-4 pb-5 allMd:p-3 allMd:pb-5 bg-[var(--light-grey)] mb-8 rounded-2xl',
        isGrey && 'p-4 pb-5 allMd:p-3 allMd:pb-5 '
      )}
    >
      <div
        className={twcls(
          'flex items-center justify-between mb-4',
          isGrey && 'mb-2'
        )}
      >
        <div className={'text-xl font-medium'}>
          Совет{!isGrey && ' от Шефа'}
        </div>
        <Picture
          icon={isGrey ? quotationsRedFilled : tipImg}
          height={isGrey ? '24' : '32'}
          width={isGrey ? '24' : '32'}
        />
      </div>
      <div
        className='text-sm [&>p>a>img]:w-full [&>p>a>img]:mt-3'
        dangerouslySetInnerHTML={{ __html: tip }}
      />
    </div>
  );
});

interface RecipeAuthorProps {
  fio: string | null | undefined;
  identify: string | null | undefined;
  title: string | null | undefined;
  avatar: string | null | undefined;
}

export const RecipeAuthor: FC<RecipeAuthorProps> = observer(
  ({ fio, identify, title, avatar }) => {
    if (!fio || !identify) {
      return null;
    }
    return (
      <Link to={`/author/${identify}`} className='flex px-2 mb-8'>
        <div className='relative flex items-center group'>
          <div className='z-0 absolute -top-1 -left-1 transition-opacity opacity-0 w-[calc(100%+8px)] h-[calc(100%+8px)] rounded-xl bg-[var(--ultra-light-grey)] group-hover:opacity-100' />
          {avatar && (
            <div className='relative z-10 flex-shrink-0 mr-3 overflow-hidden rounded-full w-11 h-11'>
              <img
                src={avatar}
                className='object-contain w-full h-full'
                alt={fio}
              />
            </div>
          )}
          <div className='relative z-10 '>
            <div className='mb-1 mr-3 text-sm font-medium'>{fio}</div>
            <div className='flex items-center text-[var(--ultra-dark-grey)] text-xs'>
              <div className='mr-3 leading-none'>{title}</div>
              <div className='flex items-center leading-none w-max text-xs allMd:w-auto text-[var(--ultra-dark-grey)]'>
                Все рецепты автора
                <Picture icon={crLGOutlined} width='20' height='20' />
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }
);

interface PdfRecipeProps {
  id: string;
}

export const PdfRecipe: FC<PdfRecipeProps> = observer(({ id }) => {
  const onClick = (): void => {
    console.log(id);
    // window.open(pdf, "PRINT");
  };
  return (
    <HoveredButton
      onClick={onClick}
      wrapperClassName={twcls(`
        border
        select-none
        border-solid
        border-[var(--main-font-color)]
        rounded-lg
        w-10
        h-10
        flex
        items-center
        justify-center
        bg-white
        transition-colors
        cursor-pointer
        hover:bg-[var(--dark-red)]
        hover:border-[var(--dark-red)]
      `)}
      icon={printBlackOutlined}
      iconHover={printWhiteOutlined}
      iconHeight={20}
      iconWidth={20}
      alt='Print recipe pdf'
    />
  );
});

interface RecipeDescriptionProps {
  text: string | null | undefined;
}

export const RecipeDescription: FC<RecipeDescriptionProps> = observer(
  ({ text }) => {
    if (!text) {
      return null;
    }
    return (
      <div
        className='mb-4 text-sm'
        dangerouslySetInnerHTML={{ __html: text }}
      />
    );
  }
);

interface RecipeInfoProps {
  rating: number | null | undefined;
  date: string | null | undefined;
  comments: number | null | undefined;
  viewCount: number | null | undefined;
  // isFavorite: boolean
  // handleRecipeFavorites: (isFavorite: boolean) => () => void
  scrollToCommentsBlock: () => void;
}

export const RecipeInfo: FC<RecipeInfoProps> = observer(
  ({
    date,
    rating,
    comments,
    scrollToCommentsBlock,
    // isFavorite,
    // handleRecipeFavorites,
    viewCount
  }) => (
    <div className='flex flex-wrap items-center gap-x-5 gap-y-2'>
      <div className='flex items-center text-sm'>
        <Picture
          pictureClassName={'mr-1'}
          icon={starYellowFilled}
          height='20'
          width='20'
          alt='star rating'
        />
        {rating ?? 0}
      </div>
      {date && (
        <div className='flex items-center text-sm'>
          <Picture
            icon={calendarDGFilled}
            width='20'
            height='20'
            pictureClassName={'mr-1'}
            alt='calendar icon'
          />
          {dayjs(date).format('DD MMMM YYYY')}
        </div>
      )}
      <div
        onClick={scrollToCommentsBlock}
        className='flex items-center text-sm cursor-pointer gap-x-1'
      >
        <Picture icon={messageCircleMidGreyFilled} width='20' height='20' />
        {comments}
      </div>
      <div className='flex items-center text-sm gap-x-1'>
        <Picture
          icon={eyeDGFilled}
          width='20'
          height='20'
          alt='view count icon'
        />
        {viewCount ?? 0}
      </div>
      {/* <HoveredButton
        icon={isFavorite ? bookmarkRedFilled : bookmarkDGFilled}
        iconHover={bookmarkRedFilled}
        onClick={handleRecipeFavorites(isFavorite)}
        wrapperClassName={''}
        iconHeight={20}
        iconWidth={20}
      />
      <ShareSocialLinksNew
        link={location.href}
        offsetYDesktop={0}
        position={'bottom center'}
        trigger={
          <HoveredButton
            icon={shareDGOutlined}
            iconHover={shareDarkGreyFilled}
            iconHeight={20}
            iconWidth={20}
          />
        }
      /> */}
    </div>
  )
);

interface RecipeStatsProps {
  difficulty: number | null | undefined;
  serving: number | null | undefined;
  time: number | null | undefined;
  calories: number | null | undefined;
}

const getRecipeDifficultyTitle = (value: number): string => {
  const obj = {
    0: 'Очень простой',
    1: 'Простой',
    2: 'Средний',
    3: 'Сложный',
    4: 'Очень сложный'
  };
  return obj[value as keyof typeof obj];
};

export const RecipeStats: FC<RecipeStatsProps> = observer(
  ({ difficulty, serving, time, calories }) => (
    <div className='flex flex-wrap items-center mb-4 text-sm gap-x-6 gap-y-2 allMd:gap-x-2.5'>
      <div className='flex items-center gap-x-1'>
        <Picture icon={barChart2MidGreyFilled} width='20' height='20' />
        {/* {Array.from({ length: difficulty ?? 0 }, (_, i) => (
          <div key={i} className='rounded-full w-2 h-2 bg-[var(--dark-red)]' />
        ))}
        {Array.from({ length: 5 - (difficulty ?? 0) }, (_, i) => (
          <div key={i} className='rounded-full w-2 h-2 bg-[var(--mid-grey)]' />
        ))} */}
        <div className='ml-1 '>{getRecipeDifficultyTitle(difficulty ?? 0)}</div>
      </div>
      <div className='flex items-center gap-x-1'>
        <Picture icon={personMidGreyFilled} width='20' height='20' />
        <div>
          {serving ?? 0}{' '}
          <span className='allMd:hidden'>
            {declension(serving ?? 0, ['порция', 'порции', 'порций'])}
          </span>
        </div>
      </div>

      <div className='flex items-center gap-x-1'>
        <Picture icon={timerMidGreyFilled} width='20' height='20' />
        {UtilsTime.minutesToStr(time ?? 0)}
      </div>
      {calories && (
        <div className='flex items-center gap-x-1'>
          <Picture icon={fireMidGreyFilled} width='20' height='20' />
          <p>
            {calories} кКал/
            <span className='text-[var(--dark-grey)]'>100г</span>
          </p>
        </div>
      )}
    </div>
  )
);

interface RecipePFCProps {
  protein: number | null | undefined;
  fat: number | null | undefined;
  carbohydrates: number | null | undefined;
  calories: number | null | undefined;
}

export const RecipePFC: FC<RecipePFCProps> = observer(
  ({ protein, fat, carbohydrates, calories }) => {
    if (!protein && !fat && !carbohydrates && !calories) {
      return null;
    }
    return (
      <div className='mb-4'>
        <p className='text-[var(--dark-grey)] text-xs mb-2'>
          Пищевая ценность на 100 г
        </p>
        <ul className='flex flex-wrap gap-2'>
          {[
            {
              id: 0,
              title: 'Белки',
              value: protein
            },
            {
              id: 1,
              title: 'Жиры',
              value: fat
            },
            {
              id: 3,
              title: 'Углеводы',
              value: carbohydrates
            },
            {
              id: 4,
              title: 'кКал',
              value: calories
            }
          ].map(item => (
            <li
              key={item.id}
              className='px-2 py-1.5 rounded-xl bg-[var(--ultra-light-grey)] min-w-[102px]'
            >
              <p className='mb-1 font-medium'>{item.value}</p>
              <p className='text-xs text-[var(--ultra-dark-grey)]'>
                {item.title}
              </p>
            </li>
          ))}
        </ul>
      </div>
    );
  }
);

interface RateRecipeBtnProps {
  _votedRating: number | null | undefined;
  toggleLeaveRecipeFeedbackModal: () => void;
}

export const RateRecipeBtn: FC<RateRecipeBtnProps> = observer(
  ({ _votedRating, toggleLeaveRecipeFeedbackModal }) => {
    const howUserHasVoted = (): string | null => {
      if (_votedRating === 1) {
        return 'Плохо';
      } else if (_votedRating === 2) {
        return 'Неплохо';
      } else if (_votedRating === 3) {
        return 'Нормально';
      } else if (_votedRating === 4) {
        return 'Хорошо';
      } else if (_votedRating === 5) {
        return 'Отлично';
      } else {
        return null;
      }
    };
    const votedText = howUserHasVoted();

    const layoutClasses = `flex
      items-center
      justify-center
      text-sm
      w-full
      allMd:w-full
      px-4.5
      h-8
      allMd:h-12`;

    if (votedText) {
      return (
        <div className={twcls(layoutClasses, 'text-[var(--ultra-dark-grey)]')}>
          Ваша оценка:&nbsp;
          <span className='text-[var(--main-font-color)]'>{votedText}</span>
        </div>
      );
    }

    return (
      <button
        onClick={toggleLeaveRecipeFeedbackModal}
        className={twcls(
          layoutClasses,
          `cursor-pointer
      border
      rounded-lg
      border-solid
      bg-white
      text-[var(--main-font-color)]
      border-[var(--main-font-color)]
      hover:text-white
      hover:bg-[var(--dark-red)]
      hover:border-[var(--dark-red)]`
        )}
      >
        Оценить рецепт
      </button>
    );
  }
);

interface RecipeImageProps {
  isFavorite: boolean;
  mainImage: string | null | undefined;
  title: string | null | undefined;
  rubric: string | null | undefined;
  video_code: string | null | undefined;
  handleRecipeFavorites: (isFavorite: boolean) => () => void;
}

export const RecipeImage: FC<RecipeImageProps> = observer(
  ({
    rubric,
    isFavorite,
    handleRecipeFavorites,
    mainImage,
    title,
    video_code
  }) => {
    const { modalsStore } = useStores();
    return (
      <div
        data-testid='recipe-hero'
        className='relative allMd:h-60 overflow-hidden rounded-2xl w-full h-[22.5rem] mb-6'
      >
        {rubric && (
          <div className='absolute w-max top-4 left-4 text-sm px-2 py-1 text-[var(--main-font-color)] rounded-lg bg-white'>
            {rubric}
          </div>
        )}

        {video_code && (
          <div
            onClick={() => {
              modalsStore.openVideoModal({ url: video_code, isDesktop: true });
            }}
            className='absolute z-50 cursor-pointer -translate-x-1/2 -translate-y-1/2 rounded-full left-1/2 top-1/2 bg-[#303030]/30 transition-colors p-4.5 hover:bg-[#303030]/70'
          >
            <Picture icon={playRoundedWhiteFilled} width='25' height='25' />
          </div>
        )}

        <RecipeActions
          isFavorite={isFavorite}
          link={location.pathname}
          favoriteHandler={handleRecipeFavorites(isFavorite)}
        />

        <img
          className='object-cover w-full h-full'
          src={mainImage ?? ''}
          alt={title ? `${title} рецепт` : 'Рецепт'}
        />
      </div>
    );
  }
);

interface RecipeImageMobileProps {
  isFavorite: boolean;
  mainImage: string | null | undefined;
  title: string | null | undefined;
  rating: number | null | undefined;
  date: string | null | undefined;
  comments: number | null | undefined;
  viewCount: number | null | undefined;
  rubric: string | null | undefined;
  video_code: string | null | undefined;
  isTablet?: boolean;
  scrollToCommentsBlock: () => void;
  handleRecipeFavorites: (isFavorite: boolean) => () => void;
}

export const RecipeImageMobile: FC<RecipeImageMobileProps> = observer(
  ({
    isFavorite,
    handleRecipeFavorites,
    mainImage,
    title,
    rating,
    date,
    comments,
    viewCount,
    rubric,
    scrollToCommentsBlock,
    video_code,
    isTablet
  }) => {
    const { modalsStore } = useStores();

    return (
      <div
        data-testid='recipe-hero'
        className='relative w-full mb-5 overflow-hidden h-52 rounded-2xl'
      >
        {video_code && (
          <div
            onClick={() => {
              modalsStore.openVideoModal({ url: video_code, isTablet });
            }}
            className='absolute z-50 cursor-pointer -translate-x-1/2 -translate-y-1/2 rounded-full left-1/2 top-1/2 bg-[#303030]/30 transition-colors p-4.5 hover:bg-[#303030]/70'
          >
            <Picture icon={playRoundedWhiteFilled} width='25' height='25' />
          </div>
        )}
        <div className='absolute top-0 left-0 w-full h-full bg-black/50' />
        <div className='absolute top-0 left-0 z-10 flex flex-col w-full h-full p-4'>
          <h1
            data-testid='recipe-title'
            className='mb-4 text-2xl font-medium text-white max-w-[calc(100%-58px)]'
          >
            {title}
          </h1>
          {rubric && (
            <div className='w-max text-xs px-2 py-1 text-[var(--main-font-color)] rounded-lg bg-white'>
              {rubric}
            </div>
          )}
          <div className='flex flex-wrap items-center mt-auto text-white gap-x-5 gap-y-2'>
            <div className='flex items-center text-sm'>
              <Picture
                pictureClassName={'mr-1'}
                icon={starYellowFilled}
                height='20'
                width='20'
                alt='star rating'
              />
              {rating ?? 0}
            </div>
            {date && (
              <div className='flex items-center text-sm'>
                <Picture
                  icon={calendarWhiteFilled}
                  width='20'
                  height='20'
                  pictureClassName={'mr-1'}
                  alt='calendar icon'
                />
                {dayjs(date).format('DD MMMM YYYY')}
              </div>
            )}
            <div
              onClick={scrollToCommentsBlock}
              className='flex items-center text-sm cursor-pointer gap-x-1'
            >
              <Picture icon={messageCircleWhiteFilled} width='20' height='20' />
              {comments}
            </div>

            <div className='flex items-center text-sm gap-x-1'>
              <Picture
                icon={eyeWhiteFilled}
                width='20'
                height='20'
                alt='view count icon'
              />
              {viewCount ?? 0}
            </div>
          </div>
        </div>
        <div className='relative z-20'>
          <RecipeActions
            isFavorite={isFavorite}
            link={location.pathname}
            favoriteHandler={handleRecipeFavorites(isFavorite)}
          />
        </div>
        <img
          className='object-cover w-full h-full'
          src={mainImage ?? ''}
          alt={title ? `${title} рецепт` : 'Рецепт'}
        />
      </div>
    );
  }
);

interface RecipeMobileTabsProps {
  recipeIngredients: NonNullable<
    GetRecipeByIdQuery['getRecipeById']
  >['recipeIngredients'];
  recipeInstructions: NonNullable<
    GetRecipeByIdQuery['getRecipeById']
  >['recipeInstructions'];
  isTablet?: boolean;
  video_code: string | null | undefined;
  isWithoutPhotos?: boolean;
}

export const RecipeMobileTabs: FC<RecipeMobileTabsProps> = observer(
  ({
    recipeIngredients,
    recipeInstructions,
    isTablet,
    video_code,
    isWithoutPhotos
  }) => (
    <Tabs defaultIndex={0}>
      <TabList className='mb-4 grid grid-cols-2 rounded-lg p-0.5 gap-x-2 bg-[var(--light-grey)] text-[var(--ultra-dark-grey)]'>
        <Tab
          key={0}
          className='w-full py-2 text-center rounded-lg'
          selectedClassName='bg-white text-[#363636]'
        >
          Приготовление
        </Tab>
        <Tab
          key={1}
          className='w-full py-2 text-center rounded-lg'
          selectedClassName='bg-white text-[#363636]'
        >
          Ингредиенты
        </Tab>
      </TabList>
      <TabPanel>
        {!!recipeInstructions?.length && (
          <RecipeSteps
            isMobile
            video_code={video_code}
            isWithoutPhotos={isWithoutPhotos}
            steps={recipeInstructions}
          />
        )}
      </TabPanel>
      <TabPanel>
        <RecipeIngredients
          isTablet={isTablet}
          ingredients={recipeIngredients}
        />
      </TabPanel>
    </Tabs>
  )
);

export const RecipeIsPromo = observer(() => (
  <div className='flex items-center gap-x-3'>
    <Link
      to={'/recipes/r/productPromo/1'}
      onClick={e => {
        e.stopPropagation();
      }}
      className='text-sm leading-[18px] px-2 py-1 rounded-lg flex items-center bg-[var(--mid-yellow)]'
    >
      <Picture
        icon={timerRedFilled}
        width={16}
        height={16}
        pictureClassName={'mr-1'}
      />
      5 минут
    </Link>

    <Link
      to={'/recipes/r/productPromo/1'}
      onClick={e => {
        e.stopPropagation();
      }}
      className='text-sm leading-[18px] px-2 py-1 rounded-lg bg-[var(--mid-yellow)]'
    >
      Готовим как Шеф
    </Link>
  </div>
));
